import { useQuery } from "@tanstack/react-query";
import { AD_CHANNEL_LABEL, AD_PRODUCTS } from "@demandhub-cms/shared";
import { aiApi, contentApi, socialApi } from "../lib/api";
import { useAdDrafts } from "./useAdDrafts";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";

/** Everything that is waiting for someone to approve, from every part of the CMS. Add a new kind here and Review, Home and the menu badge all pick it up. */
export type ReviewKind = "content" | "social" | "ai_draft" | "ad";

export interface ReviewItem {
  key: string;
  kind: ReviewKind;
  id: string;
  title: string;
  /** Short line under the title, e.g. the channel for a social post. */
  detail: string;
  updatedAt: string;
  /** Where the person goes to look at it. */
  to: string;
  /** Who wrote it, when the source says (used to stop people approving their own work). */
  createdBy: string | null;
}

export const REVIEW_KIND_LABEL: Record<ReviewKind, string> = { content: "Content", social: "Social post", ai_draft: "AI draft", ad: "Ad" };

const LIMIT = 50;

export function useReviewInbox() {
  const { can } = useAuth();
  const { currentSiteId } = useSite();
  const on = !!currentSiteId && can("content.publish");
  const base = { enabled: on, staleTime: 30_000, refetchOnWindowFocus: true } as const;

  const content = useQuery({
    queryKey: ["content-list", currentSiteId, "pending_review", "inbox"],
    ...base,
    queryFn: () => contentApi.list({ siteId: currentSiteId, status: "pending_review", page: 1, pageSize: LIMIT }),
  });
  const social = useQuery({
    queryKey: ["social-posts", currentSiteId, "pending_review"],
    ...base,
    queryFn: () => socialApi.posts(currentSiteId, "pending_review"),
  });
  const drafts = useQuery({
    queryKey: ["ai-proposals", currentSiteId, "article_draft", "pending", "inbox"],
    ...base,
    enabled: on && can("ai.use"),
    queryFn: () => aiApi.proposals({ siteId: currentSiteId, kind: "article_draft", status: "pending", page: 1, pageSize: LIMIT }),
  });
  const ads = useAdDrafts({ siteId: currentSiteId, status: "in_review" }, { ...base, enabled: on && can("ai.use") });

  const items: ReviewItem[] = [
    ...(content.data?.items ?? []).map((c): ReviewItem => ({
      key: `content-${c.id}`,
      kind: "content",
      id: c.id,
      title: c.title || "Untitled",
      detail: c.type.replace("_", " "),
      updatedAt: c.updatedAt,
      to: `/content/${c.id}`,
      createdBy: null,
    })),
    ...(social.data?.items ?? []).map((p): ReviewItem => ({
      key: `social-${p.id}`,
      kind: "social",
      id: p.id,
      title: p.text.length > 90 ? `${p.text.slice(0, 90)}…` : p.text,
      detail: p.channelName,
      updatedAt: p.updatedAt,
      to: `/review?queue=posts&post=${p.id}`,
      createdBy: p.createdBy,
    })),
    ...(drafts.data?.items ?? []).map((d): ReviewItem => ({
      key: `ai-${d.id}`,
      kind: "ai_draft",
      id: d.id,
      title: (d.output as { title?: string } | null)?.title ?? "AI draft",
      detail: "Written by AI",
      updatedAt: d.createdAt,
      to: `/ai/drafts/${d.id}`,
      createdBy: null,
    })),
    ...(ads.data?.items ?? []).map((d): ReviewItem => ({
      key: `ad-${d.id}`,
      kind: "ad",
      id: d.id,
      title: d.payload.channel === "linkedin" ? d.payload.headline || d.payload.postText.slice(0, 90) : d.payload.headlines[0] || d.payload.adGroup || "Google ad",
      detail: `${AD_PRODUCTS[d.product].name} · ${AD_CHANNEL_LABEL[d.channel]}`,
      updatedAt: d.reviewedAt ?? d.createdAt,
      to: `/review?queue=ads&ad=${d.id}&product=${d.product}&channel=${d.channel}`,
      createdBy: d.addedByType === "human" ? d.addedBy : null,
    })),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  const total = (content.data?.total ?? 0) + (social.data?.total ?? 0) + (drafts.data?.total ?? 0) + (ads.data?.items.length ?? 0);
  const counts: Record<ReviewKind, number> = { content: content.data?.total ?? 0, social: social.data?.total ?? 0, ai_draft: drafts.data?.total ?? 0, ad: ads.data?.items.length ?? 0 };
  return {
    items,
    total,
    counts,
    /** The raw ad drafts, so the review panel can open one without another request. */
    adDrafts: ads.data?.items ?? [],
    isLoading: content.isLoading || social.isLoading || drafts.isLoading || ads.isLoading,
    /** Kinds that could not be loaded. The rest still show, so one broken source never hides the others. */
    failed: [content.isError && "content", social.isError && "social posts", drafts.isError && "AI drafts", ads.isError && "ads"].filter((x): x is string => !!x),
    refetch: () => Promise.all([content.refetch(), social.refetch(), drafts.refetch(), ads.refetch()]),
  };
}
